// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductPriceAndActions, { ProductPriceAndActionsStyle } from "@sc/App/Components/Product/ProductPriceAndActions";
import { CustomAppState } from "@sc/App/Store/Reducers";
import { ProductListProductInformationStyles } from "@sc/Overrides/Widgets/ProductList/ProductListProductInformation";

import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@insite/content-library/Widgets/ProductList/ProductListProductInformation";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    productInformationStyles?: ProductListProductInformationStyles;
    productListProductImage?: ProductListProductImageStyles;
    productPriceAndActionsStyles?: ProductPriceAndActionsStyle;
    productPriceAndActionsTopSectionStyles?: ProductPriceAndActionsStyle;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
    },
    searchScoreDataGridItem: {
        width: 12,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    const { isTabletScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));
    const {
        showAvailability,
        showPrice,
        showTextBadges,
        maximumNumberOfTextBadges,
        showImageBadges,
        maximumNumberOfImageBadges,
        showAddToList,
        showCompare,
    } = props;
    return (
        <StyledWrapper {...styles.gridContainer} data-test-selector={`productListProductCard${props.product.id}`}>
            <StyledWrapper {...styles.leftColumnGridItem}>
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={false}
                    showImageBadge={false}
                    showTextBadge={props.showTextBadges}
                    // XNG-Changes
                    {...{ extendedStyles: styles.productListProductImage }}
                />
            </StyledWrapper>
            <StyledWrapper {...styles.infoGridItem} className="product-description">
                <ProductListProductInformation
                    showBrand={props.showBrand}
                    showTitle={props.showTitle}
                    showPartNumbers={props.showPartNumbers}
                    showAvailability={false}
                    showAttributes={props.showAttributes}
                    showImageBadges={!isTabletScreen && props.showImageBadges}
                    maxImageBadges={props.maximumNumberOfImageBadges}
                    showTextBadges={!isTabletScreen && props.showTextBadges}
                    maxTextBadges={props.maximumNumberOfTextBadges}
                    {...{ extendedStyles: styles.productInformationStyles }}
                />
                {isTabletScreen && (
                    <ProductPriceAndActions
                        showAvailability={showAvailability}
                        showAddToList={false}
                        showPrice={showPrice}
                        showTextBadges={showTextBadges}
                        maximumNumberOfTextBadges={maximumNumberOfTextBadges}
                        showImageBadges={showImageBadges}
                        maximumNumberOfImageBadges={maximumNumberOfImageBadges}
                        showAddToCartActions={false}
                        showOriginPrice={false}
                        containerClassName="pricing-info-container"
                        extendedStyles={styles.productPriceAndActionsTopSectionStyles}
                    />
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.actionsGridItem}>
                <ProductPriceAndActions
                    showAvailability={!isTabletScreen && showAvailability}
                    showAddToList={showAddToList}
                    showPrice={!isTabletScreen && showPrice}
                    showTextBadges={false}
                    showImageBadges={false}
                    showCompare={showCompare}
                    extendedStyles={styles.productPriceAndActionsStyles}
                    containerClassName="pricing-info-container"
                />
            </StyledWrapper>
            <StyledWrapper {...styles.searchScoreDataGridItem}>
                <ProductSearchScoreData />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductCard);
